import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';

import { MicrosoftHealthCheckBanner_MicrosoftGccHighFragment } from 'src/generated/mloop-graphql';

import AppWideBanner, { AppWideBannerAction } from '../AppWideBanner';

interface Fragments {
  microsoftGccHigh: MicrosoftHealthCheckBanner_MicrosoftGccHighFragment;
}

interface Props {
  retry: AppWideBannerAction;
}

const MicrosoftGccHighHealthCheckBanner: FCWithFragments<Fragments, Props> = ({ microsoftGccHigh, retry }) => {
  const { t } = useTranslation();
  const microsoftGccHighEnabled = useFlag('org_microsoft_gcc_high');
  // hide if its not installed or is healthy
  if (!microsoftGccHighEnabled || !microsoftGccHigh || !microsoftGccHigh?.isInstalled || microsoftGccHigh?.isHealthy) {
    return null;
  }

  const issues = [
    !microsoftGccHigh.canReadDirectory && t('hiring-dashboard-nav:nav.banner.endpoint.employeeDirectory'),
    !microsoftGccHigh.canReadCalendars && t('hiring-dashboard-nav:nav.banner.endpoint.calendars'),
    !microsoftGccHigh.canReadMeetingRooms && t('hiring-dashboard-nav:nav.banner.endpoint.meetingRooms'),
  ].filter(Boolean) as string[];

  const text =
    issues.length > 0
      ? t('hiring-dashboard-nav:nav.banner.integrationUnhealthyWithEndpoints', {
          integrationName: 'Microsoft GCC High',
          endpoints: issues,
        })
      : t('hiring-dashboard-nav:nav.banner.integrationUnhealthy', { integrationName: 'Microsoft GCC High' });

  return <AppWideBanner color="foreground" backgroundColor="error" text={text} action={retry} />;
};

MicrosoftGccHighHealthCheckBanner.fragments = {
  microsoftGccHigh: gql`
    fragment MicrosoftHealthCheckBanner_microsoftGccHigh on MicrosoftIntegrationHealth {
      isHealthy
      isInstalled
      canReadDirectory
      canReadCalendars
      canReadMeetingRooms
    }
  `,
};

export default MicrosoftGccHighHealthCheckBanner;
